import { Columns03, FilterLines, SearchLg } from '@untitled-ui/icons-react/build/cjs';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import BulkActionBar from 'src/components/BulkActionBar';
import ButtonCmp from 'src/components/ButtonCmp';
import CheckBox from 'src/components/CheckBox';
import CustomPagination from 'src/components/CustomPagination';
import DateRangePicker from 'src/components/DateRangePicker';
import DraggableLayout from 'src/components/DraggableLayout';
import InputText from 'src/components/InputText/InputText';
import NotFoundUI from 'src/components/NotFoundUI';
import SelectBox from 'src/components/SelectBox/SelectBox';
import TableCmp from 'src/components/TableCmp';
import TableTabButton from 'src/components/TableTabButton';
import { currentUser as currentUserSelector } from 'src/redux/auth.slice';
import { setInvoiceColumns } from 'src/redux/basic.slice';
import { fetchInvoices } from 'src/redux/documentHub.slice';
import { useAppSelector } from 'src/redux/hooks';
import { AppDispatch, RootState } from 'src/redux/store';
import { updateTableColumnSettings } from 'src/services/QuoteService';
import { createDateWithoutTimezoneConversion } from 'src/utils/CommonFunctions';

import InvoiceDraggableCell from './components/InvoiceDraggableCell';
import InvoiceLoaderRow from './components/InvoiceLoaderRow';
import InvoiceRow from './components/InvoiceRow';
import RangePopover from './components/RangePopover';
import { BULK_SELECTION_INITIAL_STATE, DAYS_OVERDUE_BUCKET_OPTIONS, INVOICE_TABLE_ALIAS, SORTABLE_INITIAL_STATE } from './documentHub.const';
import { formatInvoiceRowData, transformInvoiceColumnsToHeadCells } from './documentHub.helper';
import FilterLayout from './FilterLayout';
import InvoiceSummaryModal from './Modals/InvoiceSummaryModal';
import PodDetailModal from './Modals/PodDetailModal';

const recordsPerPageArray = [
    { label: '10', value: 10 },
    { label: '25', value: 25 },
    { label: '50', value: 50 },
    { label: '100', value: 100 },
];

const statusOptions = [
    { label: 'Paid', value: 'paid' },
    { label: 'Pending', value: 'pending' },
    { label: 'Overdue', value: 'overdue' },
    { label: 'Partial', value: 'partial' },
];

// The filter-bar fields only — every value Cancel resets. Sorting, page size and pagination are
// driven from outside the bar, so they are deliberately not part of this.
const FILTER_DEFAULTS = {
    search: '',
    status: 'all',
    dueDateStart: null,
    dueDateEnd: null,
    billingAccount: 'all',
    daysOverdueBucket: '',
    minAmount: '',
    maxAmount: '',
};

interface InvoiceListViewProps {
    activeTab: string;
    setActiveTab: (tab: string) => void;
    tabsData: any[];
}

const InvoiceListView: React.FC<InvoiceListViewProps> = ({ activeTab, setActiveTab, tabsData }) => {
    const { t } = useTranslation();
    const dispatch = useDispatch<AppDispatch>();

    const { invoices, invoicesTotalRecords, invoicesLoading } = useSelector((state: RootState) => state.documentHub);

    const initParams = {
        // Filter bar additions (2026-09-16) — mirror WAL Receivables filters; all AND-ed with search on the BE.
        ...FILTER_DEFAULTS,
        sortType: 'desc',
        // Client decision — land sorted by Last Updated (newest first), matching the visible column.
        sortField: 'lastUpdated',
        page: 1,
        limit: 25,
    };

    const [params, setParams] = useState(initParams);
    const [searchValue, setSearchValue] = useState('');
    const [selectedInvoice, setSelectedInvoice] = useState<any>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    // POD preview opened from the row's POD icon — same modal the POD tab uses.
    const [selectedPod, setSelectedPod] = useState<any>(null);
    const [isFilterView, setIsFilterView] = useState(false);
    const [filterCount, setFilterCount] = useState(0);

    // Due date range picker state (the Invoice Date picker was removed at the client's request, 2026-09-16)
    const [dueDateStart, setDueDateStart] = useState<any>(null);
    const [dueDateEnd, setDueDateEnd] = useState<any>(null);
    const [selectedDueTimeRange, setSelectedDueTimeRange] = useState<string>('custom');

    // Billing Account options come from the logged-in user's divisions (same source the Track
    // page uses) — the portal has no separate billing-account endpoint and none is needed.
    const currentUser = useAppSelector(currentUserSelector);
    const billingAccountOptions = useMemo(
        () =>
            (currentUser?.divisions ?? []).flatMap((div: any) =>
                (div.customerBillingAccount ?? []).map((acc: any) => ({
                    value: acc.id,
                    label: acc.displayName,
                }))
            ),
        [currentUser?.divisions]
    );

    // Column rearrangement state
    const [isRowSortable, setIsRowSortable] = useState(SORTABLE_INITIAL_STATE);
    const [columnsOrder, setColumnsOrder] = useState<any[]>([]);
    const invoiceColumns = useSelector((state: RootState) => state.basic.invoiceColumns);

    // Bulk selection state
    const [bulkSelection, setBulkSelection] = useState(BULK_SELECTION_INITIAL_STATE);
    const selectedIds = bulkSelection.data;

    const [showAllSelected, setShowAllSelected] = useState(false);

    const setSelectedIds = useCallback((ids: number[]) => {
        if (ids.length === 0) setShowAllSelected(false);
        setBulkSelection({ isOpen: ids.length > 0, data: ids });
    }, []);

    const handleToggleSelect = useCallback((id: number) => {
        setBulkSelection(({ data }) => {
            const next = data.includes(id) ? data.filter((i) => i !== id) : [...data, id];
            if (next.length === 0) setShowAllSelected(false);

            return { isOpen: next.length > 0, data: next };
        });
    }, []);

    const displayedInvoices = useMemo(() => (showAllSelected ? invoices.filter((inv: any) => selectedIds.includes(inv.id)) : invoices), [showAllSelected, invoices, selectedIds]);

    // Skip the first render — index.tsx already fetched on mount.
    const isFirstRender = React.useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;

            return;
        }
        dispatch(fetchInvoices(params));
    }, [params]);

    useEffect(() => {
        let count = 0;
        if (params.status !== 'all') count += 1;
        if (params.dueDateStart && params.dueDateEnd) count += 1;
        if (params.billingAccount && params.billingAccount !== 'all') count += 1;
        if (params.daysOverdueBucket) count += 1;
        if (params.minAmount || params.maxAmount) count += 1;
        setFilterCount(count);
    }, [params]);

    const headCells = useMemo(() => {
        const hasData = invoices.length > 0 && !invoicesLoading;
        const result = transformInvoiceColumnsToHeadCells(invoiceColumns, hasData, t);

        const checkboxCell = {
            id: 'checkbox',
            name: (
                <CheckBox
                    id="selectAllInvoices"
                    checked={false}
                    onChangeFunc={() => setSelectedIds(invoices.map((inv: any) => inv.id).filter(Boolean))}
                    name="selectAllInvoices"
                    value="selectAllInvoices"
                    disabled={invoicesLoading}
                />
            ),
            sortable: false,
            visible: true,
            rowClassName: 'w-[40px] min-w-[40px] max-w-[40px] sticky left-0 bg-inherit',
        };

        return [checkboxCell, ...result];
    }, [invoices, invoicesLoading, t, invoiceColumns, setSelectedIds]);

    const searchDebounce = useCallback(
        debounce((debSearch: string) => {
            setParams((old: any) => ({ ...old, search: debSearch, page: 1 }));
        }, 700),
        []
    );

    const handleFilter = (event: any) => {
        const { value } = event.target;
        if (value !== '' && value.trim() === '') return;
        setSearchValue(value);
        searchDebounce(value.trim());
    };

    // Single-field filter setter (SelectBox / RangePopover) — always resets to page 1
    const onFilterChange = (field: string, value: any) => {
        setParams((old: any) => ({ ...old, [field]: value, page: 1 }));
    };

    const toRangeParam = (date: any, type?: string) => (date ? (type === 'custom' ? moment(date).local().format('YYYY-MM-DD') : moment.utc(date).local().format('YYYY-MM-DD')) : null);

    const handleDueDatePickerChange = (dates: any, type?: string) => {
        const [start, end] = dates;

        setDueDateStart(start);
        setDueDateEnd(end);
        setSelectedDueTimeRange(type ?? 'custom');

        setParams((old: any) => ({ ...old, dueDateStart: toRangeParam(start, type), dueDateEnd: toRangeParam(end, type), page: 1 }));
    };

    // Prev/next arrows on the Due Date picker shift the selected window by its own length.
    // No "today" cap — due dates legitimately sit in the future.
    const getDueDateFilterRange = (type?: string) => {
        let startDT: any;
        let endDT: any;
        const daysDiff = moment(dueDateEnd).diff(moment(dueDateStart), 'days') + 1;

        if (type === 'next') {
            const next = moment(dueDateEnd).add(1, 'day');
            startDT = next.toDate();
            endDT = moment(next)
                .add(daysDiff - 1, 'day')
                .toDate();
        } else if (type === 'prev') {
            const prev = moment(dueDateStart).subtract(daysDiff, 'day');
            startDT = prev.toDate();
            endDT = moment(prev)
                .add(daysDiff - 1, 'day')
                .toDate();
        }

        if (startDT && endDT) {
            setDueDateStart(startDT);
            setDueDateEnd(endDT);
            setParams((old: any) => ({
                ...old,
                dueDateStart: moment(startDT).format('YYYY-MM-DD'),
                dueDateEnd: moment(endDT).format('YYYY-MM-DD'),
                page: 1,
            }));
        }
    };

    const handleRowClick = (invoiceData: any) => {
        // POD-only rows (order has a POD but no invoice) have no invoice to summarize —
        // clicking them opens the POD modal instead.
        if (!invoiceData?.id) {
            if (invoiceData?.podDocuments?.length) setSelectedPod(invoiceData);

            return;
        }
        setSelectedInvoice(invoiceData);
        setIsModalOpen(true);
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedInvoice(null);
    };

    // PodDetailModal takes the full invoice row — it needs podDocuments for the preview tabs AND
    // the invoice id to fetch the order context (addresses, order details) for its details tab.
    const handlePodClick = (invoiceData: any) => {
        if (!invoiceData?.podDocuments?.length) return;
        setSelectedPod(invoiceData);
    };

    const handleClosePodModal = () => {
        setSelectedPod(null);
    };

    const handleTabClick = (tab: any) => () => {
        if (tab?.value === activeTab) {
            return;
        }
        setActiveTab(tab?.value);
    };

    useEffect(() => {
        if (!isFilterView) return;

        const handleScroll = () => {
            document.body.dispatchEvent(new MouseEvent('mousedown', { bubbles: true }));
        };

        window.addEventListener('scroll', handleScroll, true);

        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, [isFilterView]);

    // Cancel closes the filter bar and discards every filter applied in it. All the filter controls
    // live inside the bar, so anything left applied after closing would keep the list filtered with
    // no visible way to clear it (only the count badge would hint at it).
    const handleFilterView = (isShow: boolean) => () => {
        setIsFilterView(isShow);

        if (isShow) return;

        setSearchValue('');
        searchDebounce.cancel();

        setDueDateStart(null);
        setDueDateEnd(null);
        setSelectedDueTimeRange('custom');

        setParams((old: any) => {
            const isAlreadyDefault = Object.keys(FILTER_DEFAULTS).every((key) => old[key] === (FILTER_DEFAULTS as any)[key]);

            if (isAlreadyDefault) return old;

            return { ...old, ...FILTER_DEFAULTS, page: 1 };
        });
    };

    // Column rearrangement handlers
    const handleSortable =
        (isOpen: boolean = true) =>
        () => {
            if (isOpen) {
                const formattedData = formatInvoiceRowData(invoices.length ? invoices : []);

                if (formattedData.length > 0) {
                    const sorted = invoiceColumns
                        .map((column: any) => {
                            const found = formattedData[0].find((f: any) => f.id === column.id);

                            return found ? { ...found, visible: column.visible } : null;
                        })
                        .filter(Boolean);

                    setIsRowSortable({ isOpen: true, data: [sorted] });
                }
            } else {
                setIsRowSortable(SORTABLE_INITIAL_STATE);
                setColumnsOrder([]);
            }
        };

    const handleColumnChange = (columns: any) => {
        setColumnsOrder(columns);
    };

    const hasColumnChanges = useMemo(() => {
        if (!columnsOrder || columnsOrder.length === 0) return false;

        return columnsOrder.some((col: any) => {
            const original = invoiceColumns.find((c: any) => c.id === col.id);
            if (!original) return true;

            return col.displayOrder !== original.displayOrder || col.visible !== original.visible;
        });
    }, [columnsOrder, invoiceColumns]);

    const handleColumnOrderSubmit = () => {
        // eslint-disable-next-line no-unused-vars
        const cleaned = columnsOrder.map(({ data: _d, boxWrapClassName: _b, ...rest }: any) => rest);
        dispatch(setInvoiceColumns(cleaned));
        setIsRowSortable(SORTABLE_INITIAL_STATE);

        updateTableColumnSettings({ tableAlias: INVOICE_TABLE_ALIAS, columnSettings: cleaned }).catch(() => {});
    };

    return (
        <div className="table-box-wrap w-full relative overflow-hidden">
            <div className={`transition-all duration-300 ${isFilterView ? 'h-[90px]' : 'h-[45px]'}`}>
                {isFilterView ? (
                    <FilterLayout onSave={() => {}} isSaveBtn={false} onCancel={handleFilterView(false)} search={searchValue} handleSearchChange={handleFilter} isSaveEnable={false} isLoading={false}>
                        <>
                            <SelectBox
                                parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${params.status !== 'all' ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'}`}
                                name="status"
                                SelectDropdownWrapClass="filter-select-dropdown-xs"
                                className="form_control"
                                options={statusOptions}
                                placeholder="Status"
                                isClearable={true}
                                value={params.status && params.status !== 'all' ? statusOptions.filter((val: any) => params.status === val?.value) : null}
                                onChangeFunc={(event: any) => {
                                    setParams((old: any) => ({
                                        ...old,
                                        status: event?.value ?? 'all',
                                        page: 1,
                                    }));
                                }}
                            />

                            {/* Due Date range — no "today" cap: due dates are usually in the future */}
                            <div className="w-fit">
                                <DateRangePicker
                                    minDate={moment().subtract(2, 'years').startOf('year').toDate()}
                                    maxDate={createDateWithoutTimezoneConversion(moment().add(2, 'years'))}
                                    handleDatePickerChange={(dates: any, type?: string) => handleDueDatePickerChange(dates, type)}
                                    dateRangeWrapClass={`bg-white !rounded-md !h-[28px] ${params?.dueDateStart && params?.dueDateEnd ? '' : '!border-dashed'}`}
                                    selectedTimeRange={selectedDueTimeRange}
                                    startDate={dueDateStart}
                                    endDate={dueDateEnd}
                                    allTimeStartDate=""
                                    showYearDropdown={false}
                                    isShowDropDownIcon={false}
                                    isPrevDisabled={false}
                                    onPrevNextClick={(type: string) => getDueDateFilterRange(type)}
                                    isClearable={true}
                                    containerClassName="xls:left-0 xls:right-[unset] lg:left-[unset] lg:right-0 left-0"
                                    parentClassName=""
                                    textWrapClass="!justify-start flex-row-reverse !gap-x-1.5"
                                    calendarClass="!mr-0"
                                    placeholder={t('DocumentHub.Due_Date')}
                                    placeholderClassname="!font-semibold !text-textSecondary"
                                />
                            </div>

                            {/* Billing Account */}
                            <SelectBox
                                parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${
                                    params.billingAccount && params.billingAccount !== 'all' ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'
                                }`}
                                name="billingAccount"
                                SelectDropdownWrapClass="filter-select-dropdown-xs min-w-[200px]"
                                className="form_control"
                                options={billingAccountOptions}
                                placeholder={t('Common.Billing_Account')}
                                isClearable={true}
                                value={params.billingAccount && params.billingAccount !== 'all' ? billingAccountOptions.filter((o: any) => String(o.value) === String(params.billingAccount)) : null}
                                onChangeFunc={(event: any) => onFilterChange('billingAccount', event?.value ?? 'all')}
                            />

                            {/* Days Outstanding (aging bucket — days past due, unpaid only) */}
                            <SelectBox
                                parentClassName={`filter-view-selectbox filter-view-selectbox-custom ${params.daysOverdueBucket ? 'filter-selectbox-with-value' : 'filter-selectbox-with-no-value'}`}
                                name="daysOverdueBucket"
                                SelectDropdownWrapClass="filter-select-dropdown-xs"
                                className="form_control"
                                options={DAYS_OVERDUE_BUCKET_OPTIONS}
                                placeholder={t('DocumentHub.Days_Outstanding')}
                                isClearable={true}
                                value={params.daysOverdueBucket ? DAYS_OVERDUE_BUCKET_OPTIONS.filter((o: any) => o.value === params.daysOverdueBucket) : null}
                                onChangeFunc={(event: any) => onFilterChange('daysOverdueBucket', event?.value ?? '')}
                            />

                            {/* Amount (total) range */}
                            <RangePopover
                                label={t('DocumentHub.Amount')}
                                minName="minAmount"
                                maxName="maxAmount"
                                minPlaceholder={t('DocumentHub.Min_Amount')}
                                maxPlaceholder={t('DocumentHub.Max_Amount')}
                                params={params}
                                onFilterChange={onFilterChange}
                                prefix="$"
                            />
                        </>
                    </FilterLayout>
                ) : (
                    <div className="table-bottom-header table-header-filter-space !gap-x-2 items-center !border-b border-utilityGray200 !flex-nowrap">
                        <div className={`table-header-bottom-left flex flex-1 scrollbar-hide ${isRowSortable.isOpen ? 'opacity-60 pointer-events-none !cursor-default' : ''}`}>
                            <div className="flex items-center gap-x-1">
                                <TableTabButton
                                    tabArray={tabsData}
                                    tabParentClassName="whitespace-nowrap scrollbar-hide"
                                    isActive={activeTab}
                                    isTabDataInClick={true}
                                    isShowAction={false}
                                    tabTextClass="!max-w-[104px] truncate"
                                    handleOnClick={handleTabClick}
                                    handleOutSideClick={() => {}}
                                    isSkeletonLoading={false}
                                    skeletonCount={2}
                                />
                            </div>
                        </div>
                        {!isRowSortable.isOpen ? (
                            <>
                                <ButtonCmp className="btn_secondary_black flex-none !px-2 !py-[5px] !gap-1" onClick={handleFilterView(true)}>
                                    <SearchLg className="w-4 h-4" />
                                    <FilterLines className="w-4 h-4" />
                                    {filterCount > 0 && <span className="ml-1 px-1.5 py-0.5 bg-primary100 text-primary700 text-xs font-semibold rounded">{filterCount}</span>}
                                </ButtonCmp>
                                <ButtonCmp
                                    className="btn_secondary_black flex-none"
                                    onClick={handleSortable()}
                                    size="square-sm"
                                    disabled={invoices.length === 0}
                                    icon={<Columns03 className="w-4 h-4" />}
                                >
                                    <></>
                                </ButtonCmp>
                                <InputText
                                    inputName="searchInvoice"
                                    placeholder={t('Common.Search')}
                                    className="bg-white focus:bg-white pl-8 pr-7 placeholder:text-gray500 shadow-sm font-normal search-input"
                                    icon={<SearchLg className="absolute top-1/2 -translate-y-1/2 left-2 text-grayText h-4 w-4" />}
                                    value={searchValue}
                                    inputType="text"
                                    onChangeFunc={handleFilter}
                                    parentClassName="table-searchInput max-mdm:flex-1 max-smd:flex-[0_0_100%] hidden flex-none"
                                    isClearable={true}
                                />
                                <div className="table-recordsPerPage flex-none">
                                    <SelectBox
                                        name="recordsPerPageGroup"
                                        id="recordsPerPageGroup"
                                        SelectDropdownWrapClass="table-recordsPerPageDropdown"
                                        selectLabelWrapClass="table-recordsPerPageSelectValue"
                                        className="form_control shadow"
                                        size="sm"
                                        options={recordsPerPageArray}
                                        onChangeFunc={(event: any) => {
                                            setParams((old: any) => ({
                                                ...old,
                                                limit: event.value,
                                                page: 1,
                                            }));
                                        }}
                                        isSearchable={false}
                                        value={recordsPerPageArray.find((val: any) => val.value === params.limit)}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <ButtonCmp type="button" onClick={handleSortable(false)} className="btn_secondary_black">
                                    {t('Common.Cancel')}
                                </ButtonCmp>
                                <ButtonCmp type="button" onClick={handleColumnOrderSubmit} disabled={!hasColumnChanges} className="btn-outline-primary">
                                    {t('Common.Save')}
                                </ButtonCmp>
                            </>
                        )}
                    </div>
                )}
            </div>

            <div className={`draggable-layout-view ${isRowSortable.isOpen ? 'slide-in' : 'slide-out'}`}>
                <DraggableLayout sortableData={isRowSortable?.data} renderCellContent={InvoiceDraggableCell} onColumnChange={handleColumnChange} />
            </div>

            <div className={`w-full table-box-container draggable-layout-view ${isRowSortable.isOpen ? 'slide-out pointer-events-none' : 'slide-in'}`}>
                {bulkSelection.isOpen && (
                    <BulkActionBar
                        selectedRows={selectedIds}
                        setSelectedRows={setSelectedIds}
                        // POD-only rows carry no invoice id — they are excluded from bulk selection
                        allIds={invoices.map((inv: any) => inv.id).filter(Boolean)}
                        showOnlySelected={showAllSelected}
                        onShowOnlySelectedChange={setShowAllSelected}
                    />
                )}

                <div className="overflow-x-auto custom-scrollbar scrollbar-hide">
                    <TableCmp
                        headCells={headCells}
                        params={params}
                        setParams={setParams}
                        tableDataArr={displayedInvoices}
                        TableLoaderRowCmp={InvoiceLoaderRow}
                        TableRowCmp={(props: any) => (
                            <InvoiceRow {...props} onRowClick={handleRowClick} onPodClick={handlePodClick} headCells={headCells} selectedIds={selectedIds} onToggleSelect={handleToggleSelect} />
                        )}
                        tableRowCmpProps={{ headCells }}
                        isBulkBarOpen={bulkSelection.isOpen}
                        numberOfSkeletonRows={20}
                        tableHeaderClass=""
                        isTableDataLoading={invoicesLoading}
                        isTableRowClickable={true}
                        tableBodyRowClass="bg-white"
                        tableRowHeight="xs"
                        isFirstColumnSticky
                    />
                </div>
                {!invoicesLoading && !invoices.length && (
                    <NotFoundUI
                        title={t('DocumentHub.No_Invoice_Found')}
                        desc={t('DocumentHub.No_Invoice_Desc')}
                        containerClassName="min-h-[calc(100%_-_172.5px)] !h-auto"
                        iconClassName="!hidden"
                        iconSecondaryClassName="!flex"
                        isEmptyBackground={true}
                        iconSecondary={<SearchLg className="w-5 h-5 text-primary" />}
                    />
                )}
            </div>

            {!isRowSortable.isOpen && (
                <div className="w-full bg-white rounded-b-xl">
                    <CustomPagination
                        recordsPerPage={params.limit}
                        totalRecords={invoicesTotalRecords}
                        currentPage={params.page}
                        handlePagination={(page: number) => {
                            setParams((old: any) => ({ ...old, page }));
                        }}
                    />
                </div>
            )}

            {selectedInvoice && <InvoiceSummaryModal invoiceData={selectedInvoice} isOpen={isModalOpen} handleClose={handleCloseModal} />}
            {selectedPod && <PodDetailModal invoiceData={selectedPod} isOpen={!!selectedPod} handleClose={handleClosePodModal} />}
        </div>
    );
};

export default InvoiceListView;
